import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ouvrirFichier } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Calendar as CalendarIcon,
  MapPin,
  Users,
  Plus,
  Trash2,
  Edit,
  Send,
  Euro,
  CheckCircle,
  XCircle,
  Clock,
  Beer,
  RefreshCw,
  ChevronRight,
  ChevronLeft,
  FileDown,
  Eye,
  AlertCircle,
  X,
  Loader2,
  Mail,
  Search,
  Truck,
  CalendarDays,
  CalendarRange,
  UserPlus,
  CalendarX,
  Lock,
  CalendarCheck,
  Link2,
  ExternalLink,
  Copy,
  FolderOpen,
  Camera,
  FileText,
  Image,
  Download,
} from "lucide-react";
import {
  format,
  parseISO,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  isToday,
  getDay,
  startOfWeek,
  endOfWeek,
} from "date-fns";
import { fr } from "date-fns/locale";
import axios from "axios";
import ProposeEventDialog from "../components/ProposeEventDialog";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PlanningUnifiePage() {
  const { user, token, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("unitaire");
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false); // Prevent double submission
  
  // Check if user is admin or bureau (full access) or just a member (limited access)
  const isBureau = user?.role === "bureau" || user?.role === "admin";
  const isMembre = user?.role === "membre";
  const hasFullAccess = isAdmin || isBureau;
  
  // Shared data
  const [events, setEvents] = useState([]);
  const [organisedEvents, setOrganisedEvents] = useState([]);
  const [foodTrucks, setFoodTrucks] = useState([]);
  const [crmClients, setCrmClients] = useState([]);
  const [availableSpecialties, setAvailableSpecialties] = useState([]);
  const [indisponibilites, setIndisponibilites] = useState([]);
  
  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showProposeDialog, setShowProposeDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  // Form states
  const [createMode, setCreateMode] = useState("direct"); // "direct" or "sondage"
  const [formData, setFormData] = useState({
    titre: "",
    client: "",
    client_crm_id: "",
    description: "",
    lieu: "",
    adresse: "",
    contact_nom: "",
    contact_telephone: "",
    couverts_estimes: "",
    nombre_food_trucks_requis: 1,
    specialites_recherchees: [],
    date_debut: "",
    date_fin: "",
    type_evenement: "unitaire",
    // Service type for unitaire events
    type_service: "midi", // "midi", "soir", "journee"
    horaire_debut: "11:00",
    horaire_fin: "15:00",
    // Multi-day
    dates: [{ 
      date: "", 
      services: [{ type: "midi", horaire_debut: "11:00", horaire_fin: "15:00", couverts_estimes: null }], 
      food_trucks: [],
      nombre_food_trucks_requis: 1
    }],
    has_bar: false,
    bar_details: "",
    frais_par_service: 20,
    bureau_exempt: true,
    rotation_enabled: false,
    // Mode de création pour Multiple
    mode_creation: "sondage", // "sondage" ou "booking_direct"
    // Direct assignment
    participants: [],
    // Responsable du planning
    responsable_planning_id: "",
    // Redevance configuration (admin/bureau only)
    type_prestation: "vente_libre",
    deal_audrey: false,
    redevance_type: "pourcentage",
    redevance_pourcentage: null,
    redevance_fixe: null,
  });
  
  // Assignment state
  const [servicePlanning, setServicePlanning] = useState({});
  const [specialtyFilter, setSpecialtyFilter] = useState("all");
  const [searchSpecialty, setSearchSpecialty] = useState("");
  const [searchFoodTruck, setSearchFoodTruck] = useState("");
  const [interestedFoodTrucks, setInterestedFoodTrucks] = useState([]);
  const [showSendSondageDialog, setShowSendSondageDialog] = useState(false);
  const [sendingSondage, setSendingSondage] = useState(false);
  const [validatingId, setValidatingId] = useState(null);
  
  // Manage participants dialog
  const [showManageParticipantsDialog, setShowManageParticipantsDialog] = useState(false);
  const [managingEvent, setManagingEvent] = useState(null);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [participantSearch, setParticipantSearch] = useState("");
  
  // Manage per-date participants (for multi-day events)
  const [showManageDateDialog, setShowManageDateDialog] = useState(false);
  const [managingDateEvent, setManagingDateEvent] = useState(null);
  const [participantsByDate, setParticipantsByDate] = useState({});
  
  // Client Portal access link
  const [clientAccessLink, setClientAccessLink] = useState(null);
  const [generatingClientLink, setGeneratingClientLink] = useState(false);
  
  // Bureau members for responsable selection
  const [bureauMembers, setBureauMembers] = useState([]);
  
  // Client Documents & Photos viewer
  const [showClientDocsDialog, setShowClientDocsDialog] = useState(false);
  const [clientDocsEvent, setClientDocsEvent] = useState(null);
  const [clientPhotos, setClientPhotos] = useState([]);
  const [clientMenu, setClientMenu] = useState(null);
  const [ftDocuments, setFtDocuments] = useState([]);
  const [loadingClientDocs, setLoadingClientDocs] = useState(false);
  
  // Pending proposals (events with status "en_attente")
  const pendingProposalEvents = useMemo(() => {
    return events.filter(e => e.statut === "en_attente");
  }, [events]);
  
  // Validate an event proposal
  const handleValidateEvent = async (eventId) => {
    try {
      setValidatingId(eventId);
      await axios.post(`${API}/api/events/${eventId}/validate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Événement validé ! Le sondage a été envoyé aux food trucks.");
      fetchData();
    } catch (error) {
      console.error("Error validating event:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la validation");
    } finally {
      setValidatingId(null);
    }
  };
  
  // Refuse an event proposal
  const handleRefuseEvent = async (eventId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir refuser cette proposition ?")) return;
    
    try {
      setValidatingId(eventId);
      await axios.delete(`${API}/api/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Proposition refusée et supprimée");
      fetchData();
    } catch (error) {
      console.error("Error refusing event:", error);
      toast.error(error.response?.data?.detail || "Erreur lors du refus");
    } finally {
      setValidatingId(null);
    }
  };
  
  // Force validate event (even without required number of food trucks)
  const handleForceValidate = async (event) => {
    const nbConfirmes = event.participants?.length || 0;
    const nbRequis = event.nombre_food_trucks_requis || 1;
    
    if (nbConfirmes < nbRequis) {
      const confirm = window.confirm(
        `Attention: Seulement ${nbConfirmes}/${nbRequis} food truck(s) confirmé(s).\n\nVoulez-vous quand même valider cet événement ?`
      );
      if (!confirm) return;
    }
    
    try {
      setValidatingId(event.id);
      await axios.post(`${API}/api/events/${event.id}/force-validate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Événement validé avec succès !");
      fetchData();
    } catch (error) {
      console.error("Error force validating:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la validation");
    } finally {
      setValidatingId(null);
    }
  };
  
  // Validate or reject a food truck position
  const handleValidatePosition = async (eventId, foodTruckId, action, reason = null) => {
    try {
      setValidatingId(`${eventId}_${foodTruckId}`);
      await axios.post(`${API}/api/events/${eventId}/validate-position`, {
        food_truck_id: foodTruckId,
        action: action,
        reason: reason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(action === "validate" ? "Positionnement validé !" : "Positionnement rejeté");
      fetchData();
    } catch (error) {
      console.error("Error validating position:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la validation");
    } finally {
      setValidatingId(null);
    }
  };
  
  // Open manage participants dialog
  const openManageParticipants = (event) => {
    setManagingEvent(event);
    setSelectedParticipants(event.participants || []);
    setParticipantSearch("");
    setShowManageParticipantsDialog(true);
  };
  
  // Save participants changes
  const handleSaveParticipants = async () => {
    if (!managingEvent) return;
    
    try {
      await axios.put(`${API}/api/events/${managingEvent.id}/participants`, {
        participants: selectedParticipants
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Participants mis à jour !");
      setShowManageParticipantsDialog(false);
      fetchData();
    } catch (error) {
      console.error("Error updating participants:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    }
  };
  
  // Toggle participant selection
  const toggleParticipant = (ftId) => {
    setSelectedParticipants(prev => {
      if (prev.includes(ftId)) {
        return prev.filter(id => id !== ftId);
      } else {
        return [...prev, ftId];
      }
    });
  };
  
  // Open manage by date dialog (for multi-day events)
  const openManageDateParticipants = (event) => {
    setManagingDateEvent(event);
    // Initialize participants by date from event data
    const initialByDate = {};
    (event.dates || []).forEach(dateItem => {
      initialByDate[dateItem.date] = dateItem.participants || event.participants || [];
    });
    setParticipantsByDate(initialByDate);
    setShowManageDateDialog(true);
  };
  
  // Toggle participant for a specific date
  const toggleDateParticipant = (date, ftId) => {
    setParticipantsByDate(prev => {
      const current = prev[date] || [];
      if (current.includes(ftId)) {
        return { ...prev, [date]: current.filter(id => id !== ftId) };
      } else {
        return { ...prev, [date]: [...current, ftId] };
      }
    });
  };
  
  // Save participants by date
  const handleSaveDateParticipants = async () => {
    if (!managingDateEvent) return;
    
    try {
      await axios.put(`${API}/api/organised-events/${managingDateEvent.id}/participants-by-date`, {
        participants_by_date: participantsByDate
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Participants par date mis à jour !");
      setShowManageDateDialog(false);
      fetchData();
    } catch (error) {
      console.error("Error updating date participants:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    }
  };
  
  // Send sondage for a specific date
  const handleSendSondageForDate = async (eventId, date) => {
    try {
      setSendingSondage(true);
      await axios.post(`${API}/api/organised-events/${eventId}/send-sondage-date`, {
        date: date
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Sondage envoyé pour le ${format(parseISO(date), "dd/MM/yyyy", { locale: fr })}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSendingSondage(false);
    }
  };
  
  // Generate client portal access link
  const handleGenerateClientLink = async (eventId, eventType = "organised") => {
    try {
      setGeneratingClientLink(true);
      const res = await axios.post(
        `${API}/api/client-portal/generate-access/${eventId}?event_type=${eventType}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      const baseUrl = window.location.origin;
      const fullLink = `${baseUrl}/client-portal?event=${eventId}&token=${res.data.access_token}`;
      
      setClientAccessLink({
        link: fullLink,
        password: res.data.password,  // Include password
        expiresAt: res.data.expires_at,
        eventTitre: res.data.event_titre,
        eventId: eventId
      });
      
      // Copy link to clipboard
      await navigator.clipboard.writeText(fullLink);
      toast.success("Lien copié ! N'oubliez pas de noter le mot de passe.");
    } catch (error) {
      console.error("Error generating client link:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la génération du lien");
    } finally {
      setGeneratingClientLink(false);
    }
  };
  
  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      // Calculate date range for indisponibilites (current month +/- 2 months)
      const dateDebut = format(subMonths(startOfMonth(currentDate), 2), "yyyy-MM-dd");
      const dateFin = format(addMonths(endOfMonth(currentDate), 2), "yyyy-MM-dd");
      
      const [eventsRes, organisedRes, ftRes, crmRes, specialtiesRes, indispoRes] = await Promise.all([
        axios.get(`${API}/api/events`, { headers }),
        axios.get(`${API}/api/organised-events`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/food-trucks`, { headers }),
        axios.get(`${API}/api/annuaire/clients/for-planning`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/specialites`).catch(() => ({ data: { specialites: [] } })),
        axios.get(`${API}/api/indisponibilites?date_debut=${dateDebut}&date_fin=${dateFin}`, { headers }).catch(() => ({ data: [] })),
      ]);
      
      setEvents(eventsRes.data || []);
      setOrganisedEvents(organisedRes.data || []);
      setFoodTrucks(ftRes.data?.filter(ft => ft.statut === "actif") || []);
      setCrmClients(crmRes.data || []); // Now includes both annuaire + CRM clients
      setAvailableSpecialties(specialtiesRes.data?.specialites || []);
      setIndisponibilites(indispoRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  }, [token, currentDate]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Load bureau members for responsable selection
  useEffect(() => {
    const loadBureauMembers = async () => {
      try {
        const response = await axios.get(`${API}/api/users/bureau-members`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBureauMembers(response.data || []);
      } catch (error) {
        console.error("Error loading bureau members:", error);
      }
    };
    if (token) loadBureauMembers();
  }, [token]);
  
  // Load client documents and photos for an event
  const loadClientDocuments = async (eventId) => {
    try {
      setLoadingClientDocs(true);
      setClientPhotos([]);
      setClientMenu(null);
      setFtDocuments([]);
      
      const [photosRes, menuRes, docsRes] = await Promise.all([
        axios.get(`${API}/api/client-portal/admin/event/${eventId}/photos`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { photos: [] } })),
        axios.get(`${API}/api/client-portal/admin/event/${eventId}/menu`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
        axios.get(`${API}/api/client-portal/admin/event/${eventId}/food-trucks-documents`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { food_trucks: [] } })),
      ]);
      
      setClientPhotos(photosRes.data?.photos || []);
      setClientMenu(menuRes.data || null);
      setFtDocuments(docsRes.data?.food_trucks || []);
    } catch (error) {
      console.error("Error loading client documents:", error);
      toast.error("Erreur lors du chargement des documents");
    } finally {
      setLoadingClientDocs(false);
    }
  };
  
  // Open client docs dialog
  const handleOpenClientDocs = (event) => {
    setClientDocsEvent(event);
    setShowClientDocsDialog(true);
    loadClientDocuments(event.id);
  };

  // Duplicate an organised event
  const handleDuplicateOrganisedEvent = (event, keepFoodTrucks = false) => {
    // Ask user if they want to keep food trucks
    if (!keepFoodTrucks && event.food_trucks?.length > 0) {
      const copyFT = window.confirm(
        `Cet événement a ${event.food_trucks.length} food truck(s) assigné(s).\n\nVoulez-vous aussi copier les assignations de food trucks ?\n\n• OK = Copier avec les food trucks\n• Annuler = Copier sans les food trucks`
      );
      if (copyFT) {
        handleDuplicateOrganisedEvent(event, true);
        return;
      }
    }

    // Prepare dates - either with food trucks or empty
    let duplicatedDates;
    if (keepFoodTrucks && event.dates?.length > 0) {
      // Copy dates with food trucks assignments
      duplicatedDates = event.dates.map(d => ({
        date: "", // Clear date so user must set new dates
        services: d.services?.map(s => ({
          type: s.type,
          horaire_debut: s.horaire_debut,
          horaire_fin: s.horaire_fin,
          couverts_estimes: s.couverts_estimes || null
        })) || [{ type: "midi", horaire_debut: "11:00", horaire_fin: "15:00" }],
        food_trucks: d.food_trucks || [], // Keep FT assignments
        nombre_food_trucks_requis: d.nombre_food_trucks_requis || null
      }));
    } else {
      // Empty dates
      duplicatedDates = [{ date: "", services: [{ type: "midi", horaire_debut: "11:00", horaire_fin: "15:00" }], food_trucks: [] }];
    }

    // Pre-fill form with event data
    setFormData({
      ...formData,
      titre: `${event.titre} (copie)`,
      client: event.client || "",
      lieu: event.lieu || "",
      adresse: event.adresse || "",
      description: event.description || "",
      nombre_food_trucks_requis: event.nombre_food_trucks_requis || 1,
      couverts_estimes: event.couverts_estimes || "",
      dates: duplicatedDates,
      has_bar: event.has_bar || false,
      bar_details: event.bar_details || "",
      specialites_recherchees: event.specialites_recherchees || [],
      frais_par_service: event.frais_par_service || 20,
      bureau_exempt: event.bureau_exempt !== false,
      contact_nom: event.contact_nom || "",
      contact_telephone: event.contact_telephone || "",
      information_complementaire: event.information_complementaire || "",
    });
    setSelectedEvent(null); // Important: not editing, creating new
    setActiveTab("multiple");
    setShowCreateDialog(true);
    
    if (keepFoodTrucks) {
      toast.success(`Copie avec ${event.food_trucks.length} food truck(s) - Modifiez les dates puis enregistrez`);
    } else {
      toast.info("Modifiez les dates puis enregistrez pour créer la copie");
    }
  };

  // Force validate an organised event
  const handleForceValidateOrganised = async (event) => {
    const nbFT = event.food_trucks?.length || 0;
    const nbRequis = event.nombre_food_trucks_requis || 1;
    
    if (nbFT < nbRequis) {
      const confirm = window.confirm(
        `Attention: Seulement ${nbFT}/${nbRequis} food truck(s) assigné(s).\n\nVoulez-vous quand même valider cet événement ?`
      );
      if (!confirm) return;
    }
    
    try {
      setValidatingId(event.id);
      await axios.post(`${API}/api/organised-events/${event.id}/validate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Événement validé avec succès !");
      fetchData();
    } catch (error) {
      console.error("Error validating organised event:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la validation");
    } finally {
      setValidatingId(null);
    }
  };
  
  // Reset form
  const resetForm = () => {
    setFormData({
      titre: "",
      client: "",
      client_crm_id: "",
      description: "",
      lieu: "",
      adresse: "",
      contact_nom: "",
      contact_telephone: "",
      couverts_estimes: "",
      nombre_food_trucks_requis: 1,
      specialites_recherchees: [],
      date_debut: "",
      date_fin: "",
      type_evenement: "unitaire",
      // Service type
      type_service: "midi",
      horaire_debut: "11:00",
      horaire_fin: "15:00",
      dates: [{ date: "", services: [{ type: "midi", horaire_debut: "11:00", horaire_fin: "15:00" }], food_trucks: [] }],
      has_bar: false,
      bar_details: "",
      frais_par_service: 20,
      bureau_exempt: true,
      rotation_enabled: false,
      participants: [],
      // Redevance
      type_prestation: "vente_libre",
      deal_audrey: false,
      redevance_type: "pourcentage",
      redevance_pourcentage: null,
      redevance_fixe: null,
    });
    setCreateMode("direct");
    setSelectedEvent(null);
  };
  
  // Handle CRM/Annuaire client selection - auto-fill address and contact
  const handleClientCrmSelect = (clientId) => {
    if (!clientId || clientId === "none") {
      setFormData({ ...formData, client_crm_id: "", client: "", adresse: "", contact_nom: "", contact_telephone: "" });
      return;
    }
    const client = crmClients.find(c => c.id === clientId);
    if (client) {
      setFormData({
        ...formData,
        client_crm_id: clientId,
        client: client.display_name || client.nom_entreprise || client.entreprise || client.nom || "",
        adresse: client.adresse || "",
        lieu: client.ville || formData.lieu,
        contact_nom: client.contact_nom || client.nom_contact || client.nom || "",
        contact_telephone: client.contact_telephone || client.telephone || "",
      });
    }
  };
  
  // Handle service type change - update default hours
  const handleServiceTypeChange = (serviceType) => {
    let horaire_debut = "11:00";
    let horaire_fin = "15:00";
    
    if (serviceType === "midi") {
      horaire_debut = "11:00";
      horaire_fin = "15:00";
    } else if (serviceType === "soir") {
      horaire_debut = "18:00";
      horaire_fin = "23:00";
    } else if (serviceType === "journee") {
      horaire_debut = "10:00";
      horaire_fin = "22:00";
    }
    
    setFormData({
      ...formData,
      type_service: serviceType,
      horaire_debut,
      horaire_fin,
    });
  };
  
  // Calendar helpers
  const daysOfWeek = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
  
  const calendarDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [currentDate]);
  
  const getEventsForDay = (day) => {
    return events.filter(event => {
      const eventDate = parseISO(event.date_debut);
      return isSameDay(eventDate, day);
    });
  };
  
  // Get indisponibilités for a specific day
  const getIndisposForDay = (day) => {
    const dateStr = format(day, "yyyy-MM-dd");
    return indisponibilites.filter(i => i.date === dateStr);
  };
  
  // Handle create event (Unitaire - Direct booking or Survey)
  const handleCreateUnitaire = async () => {
    if (isSubmitting) return; // Prevent double submission
    
    try {
      if (!formData.titre || !formData.date_debut || !formData.lieu) {
        toast.error("Veuillez remplir les champs obligatoires");
        return;
      }
      
      // Si mode sondage, type_evenement doit être "collectif" pour déclencher l'envoi des emails
      const isSondage = createMode === "sondage";
      
      // Validation: En mode Booking Direct, le nombre de FT sélectionnés doit correspondre au nombre requis
      if (!isSondage && formData.nombre_food_trucks_requis > 0) {
        if (formData.participants.length !== formData.nombre_food_trucks_requis) {
          toast.error(`Vous avez sélectionné ${formData.participants.length} food truck(s) mais ${formData.nombre_food_trucks_requis} sont requis`);
          return;
        }
      }
      
      // En mode Sondage, vérifier qu'au moins une spécialité est sélectionnée
      if (isSondage && formData.specialites_recherchees.length === 0) {
        toast.error("Veuillez sélectionner au moins une spécialité pour le sondage");
        return;
      }
      
      setIsSubmitting(true); // Lock submission
      
      const payload = {
        titre: formData.titre,
        date_debut: formData.date_debut,
        date_fin: formData.date_fin || formData.date_debut,
        lieu: formData.lieu,
        adresse: formData.adresse,
        information_complementaire: formData.description,
        type_evenement: isSondage ? "collectif" : "unitaire",
        participants: formData.participants,
        is_sondage: isSondage,
        specialites_recherchees: formData.specialites_recherchees,
        nombre_food_trucks_requis: formData.nombre_food_trucks_requis,
        // Client CRM
        client: formData.client,
        client_crm_id: formData.client_crm_id || null,
        contact_nom: formData.contact_nom,
        contact_telephone: formData.contact_telephone,
        // Responsable du planning
        responsable_planning_id: formData.responsable_planning_id || null,
        // Service type and hours
        type_service: formData.type_service,
        horaire_debut: formData.horaire_debut,
        horaire_fin: formData.horaire_fin,
        // Redevance configuration
        type_prestation: formData.type_prestation,
        deal_audrey: formData.deal_audrey,
        type_redevance: formData.deal_audrey 
          ? "pourcentage" 
          : formData.redevance_type,
        montant_redevance: formData.deal_audrey 
          ? (formData.type_prestation === "vente_libre" ? 10 : 7)
          : (formData.redevance_type === "pourcentage" ? formData.redevance_pourcentage : formData.redevance_fixe),
      };
      
      await axios.post(`${API}/api/events`, payload, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(isSondage ? "Sondage envoyé aux food trucks" : "Événement créé avec succès");
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error creating event:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setIsSubmitting(false); // Unlock submission
    }
  };
  
  // Handle create organised event (Multiple)
  const handleCreateMultiple = async () => {
    if (isSubmitting) return; // Prevent double submission
    
    try {
      if (!formData.titre || !formData.lieu || !formData.dates.some(d => d.date)) {
        toast.error("Veuillez remplir les champs obligatoires");
        return;
      }
      
      setIsSubmitting(true); // Lock submission
      
      const isBookingDirect = formData.mode_creation === "booking_direct";
      
      // Validation pour mode Booking Direct
      if (isBookingDirect) {
        for (const dateItem of formData.dates) {
          if (!dateItem.date) continue;
          const ftCount = (dateItem.food_trucks || []).length;
          const required = dateItem.nombre_food_trucks_requis || 1;
          if (ftCount !== required) {
            toast.error(`Date ${dateItem.date}: ${ftCount} FT sélectionné(s) sur ${required} requis`);
            return;
          }
        }
      }
      
      // Validation pour mode Sondage
      if (!isBookingDirect && formData.specialites_recherchees.length === 0) {
        toast.error("Veuillez sélectionner au moins une spécialité pour le sondage");
        return;
      }
      
      const payload = {
        ...formData,
        couverts_estimes: formData.couverts_estimes ? parseInt(formData.couverts_estimes) : null,
        dates: formData.dates.filter(d => d.date),
        mode_creation: formData.mode_creation || "sondage"
      };
      
      const response = await axios.post(`${API}/api/organised-events`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const eventId = response.data?.id;
      
      // Comportement selon le mode
      if (isBookingDirect) {
        // En mode Booking Direct, les emails sont envoyés automatiquement par le backend
        const emailsSent = response.data?.emails_sent || 0;
        toast.success(`Booking envoyé ! (${emailsSent} demande${emailsSent > 1 ? 's' : ''} envoyée${emailsSent > 1 ? 's' : ''})`);
      } else {
        // En mode Sondage, envoyer le sondage aux FT par spécialité
        if (eventId) {
          try {
            const sondageResponse = await axios.post(`${API}/api/organised-events/${eventId}/send-sondage`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const emailsSent = sondageResponse.data?.emails_sent || 0;
            toast.success(`Sondage envoyé ! (${emailsSent} email${emailsSent > 1 ? 's' : ''} envoyé${emailsSent > 1 ? 's' : ''})`);
          } catch (sondageError) {
            console.error("Error sending sondage:", sondageError);
            toast.success("Événement créé mais erreur lors de l'envoi du sondage");
          }
        } else {
          toast.success("Événement créé avec succès");
        }
      }
      
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error creating event:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setIsSubmitting(false); // Unlock submission
    }
  };
  
  // Delete event
  const handleDeleteEvent = async (eventId, isOrganised = false) => {
    if (!window.confirm("Supprimer cet événement ?")) return;
    
    try {
      const endpoint = isOrganised ? `${API}/api/organised-events/${eventId}` : `${API}/api/events/${eventId}`;
      await axios.delete(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Événement supprimé");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };
  
  // Send sondage - works for both regular events and organised events
  const handleSendSondage = async (eventId, isOrganised = false) => {
    try {
      setSendingSondage(true);
      
      // Use different endpoint based on event type
      const endpoint = isOrganised 
        ? `${API}/api/organised-events/${eventId}/send-sondage`
        : `${API}/api/events/${eventId}/resend-sondage`;
      
      const response = await axios.post(endpoint, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const emailsSent = response.data?.emails_sent || 0;
      toast.success(`Sondage envoyé ! (${emailsSent} email${emailsSent > 1 ? 's' : ''} envoyé${emailsSent > 1 ? 's' : ''})`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi du sondage");
    } finally {
      setSendingSondage(false);
    }
  };
  
  // Toggle specialty
  const handleSpecialtyToggle = (specialty, checked) => {
    let newSpecialties = [...formData.specialites_recherchees];
    
    if (specialty === "Toutes") {
      newSpecialties = checked ? ["Toutes"] : [];
    } else {
      newSpecialties = newSpecialties.filter(s => s !== "Toutes");
      if (checked) {
        if (!newSpecialties.includes(specialty)) newSpecialties.push(specialty);
      } else {
        newSpecialties = newSpecialties.filter(s => s !== specialty);
      }
    }
    
    setFormData({ ...formData, specialites_recherchees: newSpecialties });
  };
  
  // Toggle food truck participant
  const handleParticipantToggle = (ftId, checked) => {
    let newParticipants = [...formData.participants];
    if (checked) {
      if (!newParticipants.includes(ftId)) newParticipants.push(ftId);
    } else {
      newParticipants = newParticipants.filter(id => id !== ftId);
    }
    setFormData({ ...formData, participants: newParticipants });
  };
  
  // Add date for multi-day events
  const addDate = () => {
    setFormData({
      ...formData,
      dates: [...formData.dates, { 
        date: "", 
        services: [{ type: "midi", horaire_debut: "11:00", horaire_fin: "15:00", couverts_estimes: null }], 
        food_trucks: [],
        nombre_food_trucks_requis: 1
      }]
    });
  };
  
  // Remove date
  const removeDate = (index) => {
    if (formData.dates.length <= 1) return;
    const newDates = formData.dates.filter((_, i) => i !== index);
    setFormData({ ...formData, dates: newDates });
  };
  
  // Add service to date
  const addService = (dateIndex) => {
    const newDates = [...formData.dates];
    newDates[dateIndex].services.push({ type: "soir", horaire_debut: "18:00", horaire_fin: "22:00", couverts_estimes: null });
    setFormData({ ...formData, dates: newDates });
  };
  
  // Filter food trucks by search and specialty
  const filteredFoodTrucks = useMemo(() => {
    return foodTrucks.filter(ft => {
      const matchesSearch = !searchFoodTruck || 
        ft.nom_food_truck?.toLowerCase().includes(searchFoodTruck.toLowerCase()) ||
        ft.nom_commercial?.toLowerCase().includes(searchFoodTruck.toLowerCase());
      
      const matchesSpecialty = specialtyFilter === "all" || 
        ft.specialite === specialtyFilter ||
        ft.specialites?.includes(specialtyFilter);
      
      return matchesSearch && matchesSpecialty;
    });
  }, [foodTrucks, searchFoodTruck, specialtyFilter]);
  
  // Filter specialties by search
  const filteredSpecialties = availableSpecialties.filter(spec =>
    spec.toLowerCase().includes(searchSpecialty.toLowerCase())
  );
  
  // Stats
  const stats = useMemo(() => ({
    total: events.length + organisedEvents.length,
    enCours: organisedEvents.filter(e => e.statut === "en_cours").length,
    confirmes: organisedEvents.filter(e => e.statut === "confirme").length,
    foodTrucks: foodTrucks.length,
  }), [events, organisedEvents, foodTrucks]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  // ========== VUE SIMPLIFIÉE POUR LES MEMBRES (FOOD TRUCKS) ==========
  if (isMembre && !hasFullAccess) {
    // Filtrer les événements où le membre est concerné (proposeur, participant, ou demande en attente)
    const myFoodTruckId = user?.food_truck_id;
    
    const myProposedEvents = events.filter(e => e.proposeur_id === user?.id);
    const eventsWithParticipationRequests = events.filter(e => 
      e.statut === "en_attente_participation" || e.statut === "en_attente_placement"
    );
    const myConfirmedEvents = events.filter(e => 
      e.food_trucks_valides?.includes(myFoodTruckId) || 
      e.participants?.some(p => p.food_truck_id === myFoodTruckId)
    );
    
    return (
      <div className="space-y-6 p-2 sm:p-0">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-secondary">Planning</h1>
            <p className="text-sm text-muted-foreground">Proposez des événements et consultez vos services</p>
          </div>
          <Button onClick={() => setShowProposeDialog(true)} className="bg-primary hover:bg-primary-hover">
            <Plus className="w-4 h-4 mr-2" />
            Proposer un événement
          </Button>
        </div>
        
        {/* Stats rapides */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="bg-white">
            <CardContent className="p-3">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{myProposedEvents.length}</p>
                <p className="text-xs text-muted-foreground">Mes propositions</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-3">
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-600">{eventsWithParticipationRequests.length}</p>
                <p className="text-xs text-muted-foreground">En attente réponse</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-3">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{myConfirmedEvents.length}</p>
                <p className="text-xs text-muted-foreground">Confirmés</p>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Événements en attente de ma réponse */}
        {eventsWithParticipationRequests.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-yellow-600" />
                Événements en attente de votre réponse
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {eventsWithParticipationRequests.map(event => (
                <div 
                  key={event.id} 
                  className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/events/${event.id}`)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold">{event.titre}</p>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <CalendarIcon className="w-3 h-3" />
                        {event.date_debut ? new Date(event.date_debut).toLocaleDateString('fr-FR') : 'Date à définir'}
                        {event.lieu && <span className="ml-2"><MapPin className="w-3 h-3 inline" /> {event.lieu}</span>}
                      </p>
                    </div>
                    <Button size="sm" variant="outline">
                      Répondre
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
        
        {/* Mes propositions d'événements */}
        {myProposedEvents.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-blue-600" />
                Mes propositions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {myProposedEvents.map(event => (
                <div 
                  key={event.id} 
                  className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/events/${event.id}`)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold">{event.titre}</p>
                      <p className="text-sm text-muted-foreground">
                        {event.date_debut ? new Date(event.date_debut).toLocaleDateString('fr-FR') : 'Date à définir'}
                      </p>
                    </div>
                    <Badge className={
                      event.statut === "valide" ? "bg-green-100 text-green-800" :
                      event.statut === "en_attente_validation" ? "bg-yellow-100 text-yellow-800" :
                      "bg-gray-100 text-gray-800"
                    }>
                      {event.statut === "valide" ? "Validé" :
                       event.statut === "en_attente_validation" ? "En attente" :
                       event.statut}
                    </Badge>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
        
        {/* Mes services confirmés */}
        {myConfirmedEvents.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Mes services confirmés
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {myConfirmedEvents.map(event => (
                <div 
                  key={event.id} 
                  className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/events/${event.id}`)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold">{event.titre}</p>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <CalendarIcon className="w-3 h-3" />
                        {event.date_debut ? new Date(event.date_debut).toLocaleDateString('fr-FR') : ''}
                        {event.lieu && <span className="ml-2"><MapPin className="w-3 h-3 inline" /> {event.lieu}</span>}
                      </p>
                    </div>
                    <Badge className="bg-green-100 text-green-800">Confirmé</Badge>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
        
        {/* Message si aucun événement */}
        {myProposedEvents.length === 0 && eventsWithParticipationRequests.length === 0 && myConfirmedEvents.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <CalendarX className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Aucun événement pour le moment</p>
              <p className="text-sm text-muted-foreground mt-2">
                Proposez un événement ou attendez d'être invité à participer
              </p>
            </CardContent>
          </Card>
        )}
        
        {/* Dialog de proposition d'événement */}
        <ProposeEventDialog
          isOpen={showProposeDialog}
          onOpenChange={setShowProposeDialog}
          token={token}
          onEventProposed={() => {
            setShowProposeDialog(false);
            loadEvents();
            toast.success("Événement proposé avec succès !");
          }}
        />
      </div>
    );
  }
  
  // ========== VUE COMPLÈTE POUR ADMIN/BUREAU ==========
  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-secondary">Planning Unifié</h1>
          <p className="text-sm text-muted-foreground">Gérez tous vos événements et plannings</p>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <Card className="bg-white">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-lg sm:text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-lg sm:text-2xl font-bold">{stats.enCours}</p>
                <p className="text-xs text-muted-foreground">En cours</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
              </div>
              <div>
                <p className="text-lg sm:text-2xl font-bold">{stats.confirmes}</p>
                <p className="text-xs text-muted-foreground">Confirmés</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Truck className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-lg sm:text-2xl font-bold">{stats.foodTrucks}</p>
                <p className="text-xs text-muted-foreground">Food Trucks</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="w-full overflow-x-auto flex flex-nowrap gap-1 p-1 bg-[#F0EBE6] scrollbar-hide">
          <TabsTrigger value="unitaire" className="flex-shrink-0 gap-2 px-3 sm:px-4" data-testid="tab-unitaire">
            <CalendarDays className="w-4 h-4" />
            <span className="hidden sm:inline">Planning Unitaire</span>
            <span className="sm:hidden">Unitaire</span>
          </TabsTrigger>
          <TabsTrigger value="multiple" className="flex-shrink-0 gap-2 px-3 sm:px-4" data-testid="tab-multiple">
            <CalendarRange className="w-4 h-4" />
            <span className="hidden sm:inline">Planning Multiple</span>
            <span className="sm:hidden">Multiple</span>
          </TabsTrigger>
        </TabsList>
        
        {/* Tab: Planning Unitaire */}
        <TabsContent value="unitaire" className="space-y-4">
          {/* Bouton création événement unitaire */}
          <Card className="bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <CalendarDays className="w-5 h-5 text-blue-600" />
                    Créer un événement ponctuel
                  </h3>
                  <p className="text-sm text-muted-foreground">Planifiez un événement sur une date unique avec booking direct ou sondage</p>
                </div>
                <Button 
                  onClick={() => { 
                    setActiveTab("unitaire");
                    setCreateMode("direct");
                    setShowCreateDialog(true); 
                  }} 
                  className="gap-2 bg-blue-600 hover:bg-blue-700"
                  data-testid="create-unitaire-btn"
                >
                  <Plus className="w-4 h-4" />
                  Nouvel événement
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Section Validations en attente */}
          {pendingProposalEvents.length > 0 && (
            <Card className="bg-amber-50/50 border-amber-300">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
                  <AlertCircle className="w-5 h-5" />
                  Validations en attente ({pendingProposalEvents.length})
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Propositions d'événements par les membres en attente de validation
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pendingProposalEvents.map((event) => (
                    <div key={event.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-white rounded-lg border border-amber-200 gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-semibold text-secondary">{event.titre}</h4>
                          <Badge variant="outline" className="border-amber-500 text-amber-600 text-xs">
                            En attente
                          </Badge>
                          {event.is_member_proposal && (
                            <Badge className="bg-green-100 text-green-700 border-green-300 text-xs">
                              Gratuit
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1 flex-wrap">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {event.lieu}
                          </span>
                          <span className="flex items-center gap-1">
                            <CalendarIcon className="w-3 h-3" />
                            {format(parseISO(event.date_debut), "dd/MM/yyyy", { locale: fr })}
                          </span>
                        </div>
                        {event.cree_par_nom && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Proposé par : <span className="font-medium text-primary">{event.cree_par_nom}</span>
                          </p>
                        )}
                        {event.description && (
                          <p className="text-xs text-muted-foreground mt-2 italic line-clamp-2">
                            "{event.description}"
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => handleValidateEvent(event.id)}
                          disabled={validatingId === event.id}
                          data-testid={`validate-event-${event.id}`}
                        >
                          {validatingId === event.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Valider
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => handleRefuseEvent(event.id)}
                          disabled={validatingId === event.id}
                          data-testid={`refuse-event-${event.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Refuser
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/events/${event.id}`)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Calendrier</CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" onClick={() => setCurrentDate(subMonths(currentDate, 1))}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="font-medium min-w-[140px] text-center">
                    {format(currentDate, "MMMM yyyy", { locale: fr })}
                  </span>
                  <Button variant="outline" size="icon" onClick={() => setCurrentDate(addMonths(currentDate, 1))}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1">
                {daysOfWeek.map(day => (
                  <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
                    {day}
                  </div>
                ))}
                {calendarDays.map((day, i) => {
                  const dayEvents = getEventsForDay(day);
                  const dayIndispos = getIndisposForDay(day);
                  const hasIndispo = dayIndispos.length > 0;
                  const isCurrentMonth = isSameMonth(day, currentDate);
                  const isSelected = selectedDate && isSameDay(day, selectedDate);
                  
                  return (
                    <div
                      key={i}
                      onClick={() => setSelectedDate(day)}
                      className={`min-h-[60px] sm:min-h-[80px] p-1 border rounded cursor-pointer transition-colors ${
                        !isCurrentMonth ? "bg-gray-50 text-gray-400" :
                        isToday(day) ? "bg-primary/10 border-primary" :
                        isSelected ? "bg-blue-50 border-blue-400" :
                        hasIndispo ? "bg-red-50/50" :
                        "hover:bg-gray-50"
                      }`}
                    >
                      <div className={`text-xs sm:text-sm font-medium flex items-center gap-1 ${isToday(day) ? "text-primary" : ""}`}>
                        {format(day, "d")}
                        {hasIndispo && <Lock className="w-2.5 h-2.5 text-red-500" />}
                      </div>
                      <div className="space-y-0.5 mt-1">
                        {dayEvents.slice(0, 2).map((event, j) => (
                          <div
                            key={j}
                            className="text-[10px] sm:text-xs bg-primary/20 text-primary px-1 py-0.5 rounded truncate"
                            title={event.titre}
                          >
                            {event.titre}
                          </div>
                        ))}
                        {dayIndispos.length > 0 && dayEvents.length < 2 && (
                          <div className="text-[10px] sm:text-xs bg-red-100 text-red-600 px-1 py-0.5 rounded truncate flex items-center gap-0.5">
                            <CalendarX className="w-2.5 h-2.5" />
                            {dayIndispos.length} bloqué{dayIndispos.length > 1 ? "s" : ""}
                          </div>
                        )}
                        {dayEvents.length > 2 && (
                          <div className="text-[10px] text-muted-foreground">+{dayEvents.length - 2}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Selected Day Events */}
              {selectedDate && (
                <div className="mt-4 pt-4 border-t">
                  <h3 className="font-semibold mb-3">
                    Événements du {format(selectedDate, "d MMMM yyyy", { locale: fr })}
                  </h3>
                  <div className="space-y-2">
                    {getEventsForDay(selectedDate).length === 0 && getIndisposForDay(selectedDate).length === 0 ? (
                      <p className="text-muted-foreground text-sm">Aucun événement ce jour</p>
                    ) : (
                      <>
                        {getEventsForDay(selectedDate).map(event => (
                          <div key={event.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium">{event.titre}</p>
                              <p className="text-sm text-muted-foreground flex items-center gap-1">
                                <MapPin className="w-3 h-3" /> {event.lieu}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant={event.statut === "valide" ? "default" : "secondary"}>
                                {event.statut}
                              </Badge>
                              <Button variant="ghost" size="icon" onClick={() => navigate(`/events/${event.id}`)}>
                                <Eye className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                        {/* Display blocked slots */}
                        {getIndisposForDay(selectedDate).map(indispo => (
                          <div key={indispo.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                            <div className="flex items-center gap-2">
                              <Lock className="w-4 h-4 text-red-500" />
                              <div>
                                <p className="font-medium text-red-700">
                                  {indispo.is_blocked ? "Créneau bloqué" : indispo.motif || "Indisponible"}
                                </p>
                                <p className="text-sm text-red-600">
                                  {indispo.food_truck_nom || "Food Truck"}
                                  {indispo.type_blocage === "plage_horaire" && ` • ${indispo.heure_debut}-${indispo.heure_fin}`}
                                  {indispo.type_blocage === "journee" && " • Journée entière"}
                                </p>
                              </div>
                            </div>
                            <Badge variant="outline" className="border-red-300 text-red-600">
                              <CalendarX className="w-3 h-3 mr-1" />
                              Bloqué
                            </Badge>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Section En Attente de Participation */}
          {events.filter(e => e.statut === "en_attente_participation").length > 0 && (
            <Card className="border-amber-200 bg-amber-50/30">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
                  <Clock className="w-5 h-5" />
                  En attente de participation ({events.filter(e => e.statut === "en_attente_participation").length})
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Ces événements attendent les réponses des food trucks
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {events.filter(e => e.statut === "en_attente_participation").map(event => {
                    const nbRequis = event.nombre_food_trucks_requis || 1;
                    const nbInteresses = event.interested_count || event.interested_food_trucks?.length || event.votes?.filter(v => v.vote === "interested")?.length || 0;
                    const nbConfirmes = event.participants?.length || 0;
                    const nbRefuses = event.declined_count || 0;
                    const manquants = Math.max(0, nbRequis - nbConfirmes);
                    const progression = nbConfirmes > 0 ? Math.round((nbConfirmes / nbRequis) * 100) : 0;
                    
                    return (
                      <div key={event.id} className="p-4 bg-white border border-amber-200 rounded-lg shadow-sm">
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-semibold">{event.titre}</h4>
                              <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                                <Clock className="w-3 h-3 mr-1" />
                                En attente
                              </Badge>
                            </div>
                            
                            <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2 flex-wrap">
                              <span className="flex items-center gap-1">
                                <CalendarIcon className="w-3 h-3" />
                                {format(parseISO(event.date_debut), "dd MMMM yyyy", { locale: fr })}
                              </span>
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {event.lieu}
                              </span>
                            </div>
                            
                            {/* Détails de ce qui manque */}
                            <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                                <div className="text-center p-2 bg-white rounded border">
                                  <p className="text-2xl font-bold text-primary">{nbRequis}</p>
                                  <p className="text-xs text-muted-foreground">FT requis</p>
                                </div>
                                <div className="text-center p-2 bg-white rounded border">
                                  <p className="text-2xl font-bold text-blue-600">{nbInteresses}</p>
                                  <p className="text-xs text-muted-foreground">Intéressés</p>
                                </div>
                                <div className="text-center p-2 bg-white rounded border">
                                  <p className="text-2xl font-bold text-green-600">{nbConfirmes}</p>
                                  <p className="text-xs text-muted-foreground">Confirmés</p>
                                </div>
                                <div className="text-center p-2 bg-white rounded border">
                                  <p className="text-2xl font-bold text-red-500">{nbRefuses}</p>
                                  <p className="text-xs text-muted-foreground">Refusés</p>
                                </div>
                                <div className="text-center p-2 bg-white rounded border">
                                  <p className={`text-2xl font-bold ${manquants > 0 ? "text-orange-600" : "text-green-600"}`}>
                                    {manquants}
                                  </p>
                                  <p className="text-xs text-muted-foreground">Manquants</p>
                                </div>
                              </div>
                              
                              {/* Barre de progression */}
                              <div className="mt-3">
                                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                                  <span>Progression</span>
                                  <span>{progression}%</span>
                                </div>
                                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full transition-all ${progression >= 100 ? "bg-green-500" : progression >= 50 ? "bg-amber-500" : "bg-red-500"}`}
                                    style={{ width: `${Math.min(progression, 100)}%` }}
                                  />
                                </div>
                              </div>
                              
                              {/* Spécialités recherchées */}
                              {event.specialites_recherchees?.length > 0 && (
                                <div className="mt-3">
                                  <p className="text-xs text-muted-foreground mb-1">Spécialités recherchées :</p>
                                  <div className="flex flex-wrap gap-1">
                                    {event.specialites_recherchees.slice(0, 5).map(spec => (
                                      <Badge key={spec} variant="secondary" className="text-xs">{spec}</Badge>
                                    ))}
                                    {event.specialites_recherchees.length > 5 && (
                                      <Badge variant="outline" className="text-xs">+{event.specialites_recherchees.length - 5}</Badge>
                                    )}
                                  </div>
                                </div>
                              )}
                              
                              {/* Food trucks intéressés */}
                              {event.interested_food_trucks?.length > 0 && (
                                <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                                  <p className="text-xs font-medium text-green-700 mb-2">
                                    Food trucks intéressés ({event.interested_food_trucks.length}) :
                                  </p>
                                  <div className="space-y-2">
                                    {event.interested_food_trucks.map((ft, i) => (
                                      <div key={i} className="flex items-center justify-between text-sm bg-white p-2 rounded border border-green-100">
                                        <div>
                                          <span className="font-medium">{ft.nom_food_truck}</span>
                                          {ft.is_proposeur && (
                                            <Badge className="ml-2 text-xs bg-purple-100 text-purple-700">Proposeur</Badge>
                                          )}
                                          {ft.specialite && (
                                            <span className="text-xs text-muted-foreground ml-2">({ft.specialite})</span>
                                          )}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                          {ft.telephone && <span className="mr-2">📞 {ft.telephone}</span>}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex sm:flex-col gap-2">
                            <Button 
                              variant="default" 
                              size="sm" 
                              onClick={() => navigate(`/events/${event.id}`)}
                              className="gap-1"
                            >
                              <Eye className="w-4 h-4" />
                              Gérer
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleSendSondage(event.id)}
                              disabled={sendingSondage}
                              className="gap-1"
                            >
                              <RefreshCw className="w-4 h-4" />
                              Relancer
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleForceValidate(event)}
                              className="gap-1 text-green-600 border-green-600 hover:bg-green-50"
                            >
                              <CheckCircle className="w-4 h-4" />
                              Valider
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => openManageParticipants(event)}
                              className="gap-1 text-blue-600 border-blue-600 hover:bg-blue-50"
                            >
                              <UserPlus className="w-4 h-4" />
                              Modifier FT
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleOpenClientDocs(event)}
                              className="gap-1 text-purple-600 border-purple-600 hover:bg-purple-50"
                            >
                              <FolderOpen className="w-4 h-4" />
                              Docs
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleDeleteEvent(event.id)}
                              className="gap-1 text-red-600 border-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                              Supprimer
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Liste des événements unitaires */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Événements Unitaires</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {events.filter(e => e.type_evenement === "unitaire" || !e.type_evenement).slice(0, 10).map(event => (
                  <div key={event.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 border rounded-lg gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium">{event.titre}</p>
                        <Badge variant={event.statut === "valide" ? "default" : "secondary"} className="text-xs">
                          {event.statut}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1 flex-wrap">
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="w-3 h-3" />
                          {format(parseISO(event.date_debut), "dd/MM/yyyy", { locale: fr })}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {event.lieu}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {event.participants?.length || 0} FT
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => navigate(`/events/${event.id}`)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDeleteEvent(event.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {events.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">Aucun événement unitaire</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Tab: Planning Multiple */}
        <TabsContent value="multiple" className="space-y-4">
          {/* Bouton création événement multiple */}
          <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-lg">Créer un événement multi-dates</h3>
                  <p className="text-sm text-muted-foreground">Planifiez un événement sur plusieurs jours avec créneaux midi et soir</p>
                </div>
                <Button onClick={() => { setActiveTab("multiple"); setShowCreateDialog(true); }} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Nouvel événement
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Liste des événements multi-jours */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CalendarRange className="w-5 h-5" />
                Événements Multi-Jours
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {organisedEvents.map(event => (
                  <div key={event.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-lg">{event.titre}</h3>
                          <Badge variant={
                            event.statut === "confirme" || event.statut === "valide" ? "default" :
                            event.statut === "en_cours" ? "secondary" : "outline"
                          } className={event.statut === "valide" ? "bg-green-600" : ""}>
                            {event.statut === "confirme" ? "Confirmé" :
                             event.statut === "valide" ? "Validé" :
                             event.statut === "en_cours" ? "En cours" : 
                             event.statut === "en_attente_confirmation" ? "En attente confirmation" :
                             event.statut === "sondage_envoye" ? "Sondage envoyé" : event.statut}
                          </Badge>
                          {/* Mode de création */}
                          {event.mode_creation && (
                            <Badge variant="outline" className={
                              event.mode_creation === "booking_direct" 
                                ? "bg-green-50 text-green-700 border-green-200" 
                                : "bg-purple-50 text-purple-700 border-purple-200"
                            }>
                              {event.mode_creation === "booking_direct" ? "Booking Direct" : "Sondage"}
                            </Badge>
                          )}
                          {event.has_bar && (
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                              <Beer className="w-3 h-3 mr-1" />
                              Bar prévu
                            </Badge>
                          )}
                        </div>
                        
                        {/* Info validation */}
                        {event.validated_by_name && (
                          <div className="flex items-center gap-2 mt-1 text-xs text-green-600">
                            <CheckCircle className="w-3 h-3" />
                            <span>Validé par {event.validated_by_name} {event.validated_at && `le ${format(parseISO(event.validated_at), "dd/MM/yyyy à HH:mm", { locale: fr })}`}</span>
                          </div>
                        )}
                        
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2 flex-wrap">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {event.lieu}
                          </span>
                          {event.client && (
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              Client: {event.client}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Truck className="w-3 h-3" />
                            {event.nombre_food_trucks_requis} FT requis
                          </span>
                          {event.couverts_estimes && (
                            <span>{event.couverts_estimes} couverts</span>
                          )}
                        </div>
                        
                        {/* Dates et services détaillés */}
                        {event.dates?.length > 0 && (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                            <p className="text-xs font-medium text-muted-foreground mb-2">PLANNING DES DATES</p>
                            <div className="space-y-2">
                              {event.dates.map((dateItem, i) => (
                                <div key={i} className="flex items-start gap-3 text-sm">
                                  <div className="flex items-center gap-1 min-w-[100px]">
                                    <CalendarIcon className="w-3 h-3 text-primary" />
                                    <span className="font-medium">{format(parseISO(dateItem.date), "dd/MM/yyyy", { locale: fr })}</span>
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    {dateItem.services?.map((service, j) => (
                                      <Badge key={j} variant="secondary" className="text-xs">
                                        {service.type === "midi" ? "🌞 Midi" : "🌙 Soir"} {service.horaire_debut}-{service.horaire_fin}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Food trucks intéressés */}
                        {event.interested_food_trucks?.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs font-medium text-green-600 mb-1">
                              {event.interested_food_trucks.length} food truck(s) intéressé(s)
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {event.interested_food_trucks.slice(0, 5).map((ft, i) => (
                                <Badge key={i} variant="outline" className="text-xs">{ft.nom_food_truck || ft.nom}</Badge>
                              ))}
                              {event.interested_food_trucks.length > 5 && (
                                <Badge variant="outline" className="text-xs">+{event.interested_food_trucks.length - 5}</Badge>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex flex-col gap-2">
                        <Button 
                          variant="default" 
                          size="sm" 
                          onClick={() => openManageDateParticipants(event)}
                          className="gap-1"
                        >
                          <Eye className="w-4 h-4" />
                          Gérer
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleSendSondage(event.id, true)}
                          disabled={sendingSondage}
                          className="gap-1"
                        >
                          <RefreshCw className="w-4 h-4" />
                          Relancer
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleForceValidateOrganised(event)}
                          disabled={validatingId === event.id}
                          className="gap-1 text-green-600 border-green-600 hover:bg-green-50"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Valider
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openManageDateParticipants(event)}
                          className="gap-1 text-blue-600 border-blue-600 hover:bg-blue-50"
                        >
                          <UserPlus className="w-4 h-4" />
                          Modifier FT
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleOpenClientDocs(event)}
                          className="gap-1 text-purple-600 border-purple-600 hover:bg-purple-50"
                        >
                          <FolderOpen className="w-4 h-4" />
                          Docs
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDuplicateOrganisedEvent(event)}
                          className="gap-1 text-amber-600 border-amber-600 hover:bg-amber-50"
                        >
                          <Copy className="w-4 h-4" />
                          Dupliquer
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDeleteEvent(event.id, true)}
                          className="gap-1 text-red-600 border-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                          Supprimer
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {organisedEvents.length === 0 && (
                  <div className="text-center py-12">
                    <CalendarRange className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">Aucun événement multi-jours</p>
                    <Button onClick={() => { setActiveTab("multiple"); setShowCreateDialog(true); }}>
                      <Plus className="w-4 h-4 mr-2" />
                      Créer un événement
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Section En Attente de Participation - Multiple */}
          {organisedEvents.filter(e => e.statut === "en_cours" || e.statut === "sondage_envoye").length > 0 && (
            <Card className="border-amber-200 bg-amber-50/30">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
                  <Clock className="w-5 h-5" />
                  En attente de participation ({organisedEvents.filter(e => e.statut === "en_cours" || e.statut === "sondage_envoye").length})
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Ces événements multi-jours attendent les réponses des food trucks
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {organisedEvents.filter(e => e.statut === "en_cours" || e.statut === "sondage_envoye").map(event => {
                    const nbRequis = event.nombre_food_trucks_requis || 1;
                    const nbInteresses = event.interested_food_trucks?.length || 0;
                    const nbConfirmes = event.confirmed_food_trucks?.length || 0;
                    const manquants = Math.max(0, nbRequis - nbConfirmes);
                    const progression = nbConfirmes > 0 ? Math.round((nbConfirmes / nbRequis) * 100) : 0;
                    
                    return (
                      <div key={event.id} className="p-4 bg-white border border-amber-200 rounded-lg shadow-sm">
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-semibold">{event.titre}</h4>
                              <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                                <Clock className="w-3 h-3 mr-1" />
                                En attente
                              </Badge>
                              {event.has_bar && (
                                <Badge variant="outline" className="bg-amber-50 text-amber-600 border-amber-200">
                                  <Beer className="w-3 h-3 mr-1" />
                                  Bar
                                </Badge>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2 flex-wrap">
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {event.lieu}
                              </span>
                              {event.dates?.length > 0 && (
                                <span className="flex items-center gap-1">
                                  <CalendarIcon className="w-3 h-3" />
                                  {event.dates.length} date(s)
                                </span>
                              )}
                            </div>
                            
                            {/* Dates résumées */}
                            {event.dates?.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {event.dates.slice(0, 3).map((d, i) => (
                                  <Badge key={i} variant="secondary" className="text-xs">
                                    {format(parseISO(d.date), "dd/MM", { locale: fr })}
                                  </Badge>
                                ))}
                                {event.dates.length > 3 && (
                                  <Badge variant="outline" className="text-xs">+{event.dates.length - 3}</Badge>
                                )}
                              </div>
                            )}
                            
                            {/* Détails de ce qui manque */}
                            <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                                <div className="text-center p-2 bg-white rounded border">
                                  <p className="text-2xl font-bold text-primary">{nbRequis}</p>
                                  <p className="text-xs text-muted-foreground">FT requis</p>
                                </div>
                                <div className="text-center p-2 bg-white rounded border">
                                  <p className="text-2xl font-bold text-blue-600">{nbInteresses}</p>
                                  <p className="text-xs text-muted-foreground">Intéressés</p>
                                </div>
                                <div className="text-center p-2 bg-white rounded border">
                                  <p className="text-2xl font-bold text-green-600">{nbConfirmes}</p>
                                  <p className="text-xs text-muted-foreground">Confirmés</p>
                                </div>
                                <div className="text-center p-2 bg-white rounded border">
                                  <p className={`text-2xl font-bold ${manquants > 0 ? "text-red-600" : "text-green-600"}`}>
                                    {manquants}
                                  </p>
                                  <p className="text-xs text-muted-foreground">Manquants</p>
                                </div>
                              </div>
                              
                              {/* Barre de progression */}
                              <div className="mt-3">
                                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                                  <span>Progression</span>
                                  <span>{progression}%</span>
                                </div>
                                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full transition-all ${progression >= 100 ? "bg-green-500" : progression >= 50 ? "bg-amber-500" : "bg-red-500"}`}
                                    style={{ width: `${Math.min(progression, 100)}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex sm:flex-col gap-2">
                            <Button 
                              variant="default" 
                              size="sm" 
                              onClick={() => {
                                setSelectedEvent(event);
                                setShowDetailDialog(true);
                              }}
                              className="gap-1"
                            >
                              <Eye className="w-4 h-4" />
                              Gérer
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleSendSondage(event.id, true)}
                              disabled={sendingSondage}
                              className="gap-1"
                            >
                              <RefreshCw className="w-4 h-4" />
                              Relancer
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleOpenClientDocs(event)}
                              className="gap-1 text-purple-600 border-purple-600 hover:bg-purple-50"
                            >
                              <FolderOpen className="w-4 h-4" />
                              Docs
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Create Event Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Créer un Événement</DialogTitle>
          </DialogHeader>
          
          <div className="overflow-y-auto flex-1 pr-2">
          {/* Mode Selection */}
          <div className="flex gap-2 mb-4">
            <Button
              variant={activeTab === "unitaire" ? (createMode === "direct" ? "default" : "outline") : "outline"}
              size="sm"
              onClick={() => setCreateMode("direct")}
              className="flex-1"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Booking Direct
            </Button>
            <Button
              variant={createMode === "sondage" ? "default" : "outline"}
              size="sm"
              onClick={() => setCreateMode("sondage")}
              className="flex-1"
            >
              <Mail className="w-4 h-4 mr-2" />
              Envoyer Sondage
            </Button>
          </div>
          
          <div className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Titre *</Label>
                <Input
                  value={formData.titre}
                  onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                  placeholder="Nom de l'événement"
                />
              </div>
              <div>
                <Label>Client (Annuaire / CRM)</Label>
                <Select
                  value={formData.client_crm_id || "none"}
                  onValueChange={handleClientCrmSelect}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un client..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Aucun (saisie manuelle) --</SelectItem>
                    {crmClients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        <div className="flex items-center gap-2">
                          {client.source === "annuaire" && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">Annuaire</span>
                          )}
                          {client.source === "crm" && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700">CRM</span>
                          )}
                          <span>{client.display_name || client.nom_entreprise || client.nom}</span>
                          {client.ville && <span className="text-muted-foreground">({client.ville})</span>}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Client name (auto-filled or manual) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Nom du client</Label>
                <Input
                  value={formData.client}
                  onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                  placeholder="Nom du client"
                  className={formData.client_crm_id ? "bg-green-50 border-green-200" : ""}
                />
                {formData.client_crm_id && (
                  <p className="text-xs text-green-600 mt-1">Auto-rempli depuis Annuaire/CRM</p>
                )}
              </div>
              <div>
                <Label>Contact</Label>
                <Input
                  value={formData.contact_nom}
                  onChange={(e) => setFormData({ ...formData, contact_nom: e.target.value })}
                  placeholder="Nom du contact"
                  className={formData.client_crm_id ? "bg-green-50 border-green-200" : ""}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Lieu *</Label>
                <Input
                  value={formData.lieu}
                  onChange={(e) => setFormData({ ...formData, lieu: e.target.value })}
                  placeholder="Ville ou lieu"
                  className={formData.client_crm_id && formData.lieu ? "bg-green-50 border-green-200" : ""}
                />
              </div>
              <div>
                <Label>Adresse</Label>
                <Input
                  value={formData.adresse}
                  onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                  placeholder="Adresse complète"
                  className={formData.client_crm_id && formData.adresse ? "bg-green-50 border-green-200" : ""}
                />
              </div>
            </div>
            
            {/* Dates - Unitaire */}
            {activeTab === "unitaire" && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Date *</Label>
                    <Input
                      type="date"
                      value={formData.date_debut}
                      onChange={(e) => setFormData({ ...formData, date_debut: e.target.value, date_fin: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Type de service *</Label>
                    <Select
                      value={formData.type_service}
                      onValueChange={handleServiceTypeChange}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="midi">🌞 Midi (11h-15h)</SelectItem>
                        <SelectItem value="soir">🌙 Soir (18h-23h)</SelectItem>
                        <SelectItem value="journee">☀️ Journée entière (10h-22h)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Heure début</Label>
                    <Input
                      type="time"
                      value={formData.horaire_debut}
                      onChange={(e) => setFormData({ ...formData, horaire_debut: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Heure fin</Label>
                    <Input
                      type="time"
                      value={formData.horaire_fin}
                      onChange={(e) => setFormData({ ...formData, horaire_fin: e.target.value })}
                    />
                  </div>
                </div>
              </>
            )}
            
            {/* Dates - Multiple */}
            {activeTab === "multiple" && (
              <div className="space-y-3">
                {/* Mode de création */}
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <Label className="flex-shrink-0">Mode :</Label>
                  <div className="flex gap-2 flex-1">
                    <Button
                      type="button"
                      variant={formData.mode_creation === "sondage" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setFormData({ ...formData, mode_creation: "sondage" })}
                      className={formData.mode_creation === "sondage" ? "bg-purple-600 hover:bg-purple-700" : ""}
                    >
                      <Send className="w-4 h-4 mr-1" /> Envoyer Sondage
                    </Button>
                    <Button
                      type="button"
                      variant={formData.mode_creation === "booking_direct" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setFormData({ ...formData, mode_creation: "booking_direct" })}
                      className={formData.mode_creation === "booking_direct" ? "bg-green-600 hover:bg-green-700" : ""}
                    >
                      <CalendarCheck className="w-4 h-4 mr-1" /> Booking Direct
                    </Button>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <Label>Dates et Services</Label>
                  <Button variant="outline" size="sm" onClick={addDate}>
                    <Plus className="w-4 h-4 mr-1" /> Ajouter date
                  </Button>
                </div>
                {formData.dates.map((dateItem, i) => (
                  <div key={i} className="p-3 border rounded-lg space-y-2 bg-white">
                    <div className="flex items-center gap-2">
                      <Input
                        type="date"
                        value={dateItem.date}
                        onChange={(e) => {
                          const newDates = [...formData.dates];
                          newDates[i].date = e.target.value;
                          setFormData({ ...formData, dates: newDates });
                        }}
                        className="flex-1"
                      />
                      {/* Nombre de FT requis par date */}
                      <div className="flex items-center gap-1">
                        <Truck className="w-4 h-4 text-gray-500" />
                        <Input
                          type="number"
                          min="1"
                          value={dateItem.nombre_food_trucks_requis || 1}
                          onChange={(e) => {
                            const newDates = [...formData.dates];
                            newDates[i].nombre_food_trucks_requis = parseInt(e.target.value) || 1;
                            setFormData({ ...formData, dates: newDates });
                          }}
                          className="w-16"
                          title="Nombre de FT requis pour cette date"
                        />
                        <span className="text-xs text-gray-500">FT</span>
                      </div>
                      {formData.dates.length > 1 && (
                        <Button variant="ghost" size="icon" onClick={() => removeDate(i)}>
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    {dateItem.services.map((service, j) => (
                      <div key={j} className="flex items-center gap-2 pl-4 flex-wrap">
                        <Select
                          value={service.type}
                          onValueChange={(val) => {
                            const newDates = [...formData.dates];
                            newDates[i].services[j].type = val;
                            setFormData({ ...formData, dates: newDates });
                          }}
                        >
                          <SelectTrigger className="w-24">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="midi">Midi</SelectItem>
                            <SelectItem value="soir">Soir</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          type="time"
                          value={service.horaire_debut}
                          onChange={(e) => {
                            const newDates = [...formData.dates];
                            newDates[i].services[j].horaire_debut = e.target.value;
                            setFormData({ ...formData, dates: newDates });
                          }}
                          className="w-24"
                        />
                        <span>-</span>
                        <Input
                          type="time"
                          value={service.horaire_fin}
                          onChange={(e) => {
                            const newDates = [...formData.dates];
                            newDates[i].services[j].horaire_fin = e.target.value;
                            setFormData({ ...formData, dates: newDates });
                          }}
                          className="w-24"
                        />
                        {/* Couverts par service */}
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4 text-gray-400" />
                          <Input
                            type="number"
                            min="0"
                            placeholder="Couverts"
                            value={service.couverts_estimes || ""}
                            onChange={(e) => {
                              const newDates = [...formData.dates];
                              newDates[i].services[j].couverts_estimes = parseInt(e.target.value) || null;
                              setFormData({ ...formData, dates: newDates });
                            }}
                            className="w-20"
                            title="Nombre de couverts estimés"
                          />
                        </div>
                      </div>
                    ))}
                    <div className="flex items-center gap-2 pl-4">
                      <Button variant="ghost" size="sm" onClick={() => addService(i)}>
                        <Plus className="w-3 h-3 mr-1" /> Service
                      </Button>
                    </div>
                    
                    {/* Sélection directe des food trucks pour cette date - Visible en mode Booking Direct */}
                    {formData.mode_creation === "booking_direct" && (
                      <div className="mt-3 pt-3 border-t">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
                            <Truck className="w-4 h-4" />
                            Food Trucks pour cette date
                          </span>
                          {/* Indicateur de progression */}
                          <div className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                            (dateItem.food_trucks || []).length === (dateItem.nombre_food_trucks_requis || 1)
                              ? "bg-green-100 text-green-700 border border-green-300" 
                              : (dateItem.food_trucks || []).length > (dateItem.nombre_food_trucks_requis || 1)
                                ? "bg-red-100 text-red-700 border border-red-300"
                                : "bg-amber-100 text-amber-700 border border-amber-300"
                          }`}>
                            {(dateItem.food_trucks || []).length === (dateItem.nombre_food_trucks_requis || 1) ? (
                              <CheckCircle className="w-3 h-3" />
                            ) : (dateItem.food_trucks || []).length > (dateItem.nombre_food_trucks_requis || 1) ? (
                              <AlertCircle className="w-3 h-3" />
                            ) : (
                              <Clock className="w-3 h-3" />
                            )}
                            {(dateItem.food_trucks || []).length} / {dateItem.nombre_food_trucks_requis || 1} FT
                          </div>
                        </div>
                        
                        {/* Liste des FT sélectionnés */}
                        {(dateItem.food_trucks || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {(dateItem.food_trucks || []).map((ftId, ftIdx) => {
                              const ft = foodTrucks.find(f => f.id === ftId);
                              const ftName = ft?.nom_food_truck || ft?.nom_commercial || ft?.nom || "FT";
                              return (
                                <Badge key={ftIdx} variant="secondary" className="text-xs flex items-center gap-1 bg-green-50 border-green-200">
                                  {ftName}
                                  <X 
                                    className="w-3 h-3 cursor-pointer hover:text-red-500" 
                                    onClick={() => {
                                      const newDates = [...formData.dates];
                                      newDates[i].food_trucks = (newDates[i].food_trucks || []).filter(id => id !== ftId);
                                      setFormData({ ...formData, dates: newDates });
                                    }}
                                  />
                                </Badge>
                              );
                            })}
                          </div>
                        )}
                        
                        {/* Dropdown pour ajouter des FT */}
                        <Select
                          value=""
                          onValueChange={(ftId) => {
                            if (ftId && !(dateItem.food_trucks || []).includes(ftId)) {
                              const newDates = [...formData.dates];
                              newDates[i].food_trucks = [...(newDates[i].food_trucks || []), ftId];
                              setFormData({ ...formData, dates: newDates });
                            }
                          }}
                        >
                          <SelectTrigger className="w-full text-sm">
                            <SelectValue placeholder="+ Ajouter un food truck..." />
                          </SelectTrigger>
                          <SelectContent>
                            {foodTrucks
                              .filter(ft => !(dateItem.food_trucks || []).includes(ft.id))
                              .map(ft => {
                                const ftName = ft.nom_food_truck || ft.nom_commercial || ft.nom || "Food Truck";
                                return (
                                  <SelectItem key={ft.id} value={ft.id}>
                                    {ftName} {ft.specialite && `(${ft.specialite})`}
                                  </SelectItem>
                                );
                              })}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            <div>
              <Label>Nombre de food trucks requis</Label>
              <Input
                type="number"
                min="1"
                value={formData.nombre_food_trucks_requis}
                onChange={(e) => setFormData({ ...formData, nombre_food_trucks_requis: parseInt(e.target.value) || 1 })}
              />
            </div>
            
            {/* Responsable du planning */}
            <div>
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4 text-purple-600" />
                Responsable du Planning
              </Label>
              <Select
                value={formData.responsable_planning_id || "none"}
                onValueChange={(value) => setFormData({ ...formData, responsable_planning_id: value === "none" ? "" : value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Sélectionner un responsable..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Aucun responsable --</SelectItem>
                  {bureauMembers.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.display_name || member.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Le responsable sera le contact principal pour la gestion de cet événement
              </p>
            </div>
            
            {/* Options spécifiques pour événements multi-jours */}
            {activeTab === "multiple" && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Couverts estimés</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.couverts_estimes}
                      onChange={(e) => setFormData({ ...formData, couverts_estimes: e.target.value })}
                      placeholder="Ex: 500"
                    />
                  </div>
                  <div>
                    <Label>Frais par service (€)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.frais_par_service}
                      onChange={(e) => setFormData({ ...formData, frais_par_service: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </div>
                
                {/* Options Bar */}
                <div className="p-4 border rounded-lg bg-amber-50/50 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Beer className="w-5 h-5 text-amber-600" />
                      <Label className="text-base font-semibold">Bar prévu sur l'événement</Label>
                    </div>
                    <Switch
                      checked={formData.has_bar}
                      onCheckedChange={(checked) => setFormData({ ...formData, has_bar: checked })}
                    />
                  </div>
                  {formData.has_bar && (
                    <div>
                      <Label className="text-sm">Détails du bar</Label>
                      <Textarea
                        value={formData.bar_details}
                        onChange={(e) => setFormData({ ...formData, bar_details: e.target.value })}
                        placeholder="Type de bar, boissons proposées, qui le gère..."
                        rows={2}
                        className="mt-1"
                      />
                    </div>
                  )}
                </div>
                
                {/* Options supplémentaires */}
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <Label className="text-sm font-medium">Rotation automatique des food trucks</Label>
                    <p className="text-xs text-muted-foreground">Les food trucks tournent entre les services</p>
                  </div>
                  <Switch
                    checked={formData.rotation_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, rotation_enabled: checked })}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <Label className="text-sm font-medium">Bureau exempté des frais</Label>
                    <p className="text-xs text-muted-foreground">Les membres du bureau ne paient pas de frais</p>
                  </div>
                  <Switch
                    checked={formData.bureau_exempt}
                    onCheckedChange={(checked) => setFormData({ ...formData, bureau_exempt: checked })}
                  />
                </div>
                
                {/* Section Redevance - Admin/Bureau only */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-semibold text-secondary mb-3 flex items-center gap-2">
                    <Euro className="w-4 h-4 text-purple-600" />
                    Configuration Redevance
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Type de prestation</Label>
                      <Select
                        value={formData.type_prestation}
                        onValueChange={(v) => setFormData({ ...formData, type_prestation: v })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Type de prestation" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="vente_libre">Vente libre</SelectItem>
                          <SelectItem value="privatisation">Privatisation</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex items-center gap-2 pt-6">
                      <Checkbox
                        id="deal_audrey_planning"
                        checked={formData.deal_audrey}
                        onCheckedChange={(v) => {
                          if (v) {
                            const pct = formData.type_prestation === "vente_libre" ? 10 : 7;
                            setFormData({ ...formData, deal_audrey: v, redevance_type: "pourcentage", redevance_pourcentage: pct });
                          } else {
                            setFormData({ ...formData, deal_audrey: v });
                          }
                        }}
                      />
                      <Label htmlFor="deal_audrey_planning" className="text-purple-700 font-medium cursor-pointer">
                        Deal Audrey ({formData.type_prestation === "vente_libre" ? "10%" : "7%"})
                      </Label>
                    </div>
                  </div>
                  
                  {!formData.deal_audrey && (
                    <div className="grid grid-cols-2 gap-4 mt-3">
                      <div>
                        <Label>Type de redevance</Label>
                        <Select
                          value={formData.redevance_type}
                          onValueChange={(v) => setFormData({ ...formData, redevance_type: v })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pourcentage">Pourcentage (%)</SelectItem>
                            <SelectItem value="fixe">Montant fixe (€)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {formData.redevance_type === "pourcentage" ? (
                        <div>
                          <Label>Pourcentage sur CA HT (%)</Label>
                          <Input
                            type="number"
                            step="0.5"
                            min="0"
                            max="100"
                            value={formData.redevance_pourcentage || ""}
                            onChange={(e) => setFormData({ ...formData, redevance_pourcentage: e.target.value ? parseFloat(e.target.value) : null })}
                            placeholder="Ex: 10"
                            className="mt-1"
                          />
                        </div>
                      ) : (
                        <div>
                          <Label>Montant fixe (€)</Label>
                          <Input
                            type="number"
                            step="1"
                            min="0"
                            value={formData.redevance_fixe || ""}
                            onChange={(e) => setFormData({ ...formData, redevance_fixe: e.target.value ? parseFloat(e.target.value) : null })}
                            placeholder="Ex: 50"
                            className="mt-1"
                          />
                        </div>
                      )}
                    </div>
                  )}
                  
                  {formData.deal_audrey && (
                    <p className="text-sm text-purple-600 mt-2 bg-purple-50 p-2 rounded">
                      Deal Audrey : {formData.type_prestation === "vente_libre" ? "10%" : "7%"} de redevance sur le CA HT déclaré.
                    </p>
                  )}
                </div>
              </>
            )}
            
            {/* Spécialités - Pour sondage OU événements multiple */}
            {(createMode === "sondage" || activeTab === "multiple") && (
              <div>
                <Label className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Spécialités recherchées pour le sondage
                </Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Sélectionnez les types de cuisine souhaités. Un email sera envoyé aux food trucks correspondants.
                </p>
                <Input
                  placeholder="Rechercher une spécialité..."
                  value={searchSpecialty}
                  onChange={(e) => setSearchSpecialty(e.target.value)}
                  className="mb-2"
                />
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 border rounded-md max-h-48 overflow-y-auto bg-gray-50">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="spec-toutes"
                      checked={formData.specialites_recherchees.includes("Toutes")}
                      onCheckedChange={(checked) => handleSpecialtyToggle("Toutes", checked)}
                    />
                    <label htmlFor="spec-toutes" className="text-sm cursor-pointer font-semibold text-primary">
                      Toutes les spécialités
                    </label>
                  </div>
                  {filteredSpecialties.slice(0, 30).map((spec) => {
                    const isCategory = ["Salé", "Sucré", "Boissons"].includes(spec);
                    return (
                      <div key={spec} className={`flex items-center space-x-2 ${isCategory ? "col-span-2 sm:col-span-3 bg-gray-200 p-2 rounded-md mt-2 -mx-1" : ""}`}>
                        <Checkbox
                          id={`spec-${spec}`}
                          checked={formData.specialites_recherchees.includes(spec)}
                          onCheckedChange={(checked) => handleSpecialtyToggle(spec, checked)}
                          disabled={formData.specialites_recherchees.includes("Toutes")}
                        />
                        <label htmlFor={`spec-${spec}`} className={`text-sm cursor-pointer ${isCategory ? "font-bold" : ""}`}>
                          {isCategory ? `🏷️ ${spec}` : spec}
                        </label>
                      </div>
                    );
                  })}
                </div>
                {formData.specialites_recherchees.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {formData.specialites_recherchees.map(spec => (
                      <Badge key={spec} variant="secondary" className="text-xs">
                        {spec}
                        <button 
                          onClick={() => handleSpecialtyToggle(spec, false)}
                          className="ml-1 hover:text-red-500"
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Food Trucks - Pour booking direct */}
            {createMode === "direct" && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Sélectionner les food trucks</Label>
                  {/* Indicateur de progression */}
                  <div className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${
                    formData.participants.length === formData.nombre_food_trucks_requis 
                      ? "bg-green-100 text-green-700 border border-green-300" 
                      : formData.participants.length > formData.nombre_food_trucks_requis
                        ? "bg-red-100 text-red-700 border border-red-300"
                        : "bg-amber-100 text-amber-700 border border-amber-300"
                  }`}>
                    {formData.participants.length === formData.nombre_food_trucks_requis ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : formData.participants.length > formData.nombre_food_trucks_requis ? (
                      <AlertCircle className="w-4 h-4" />
                    ) : (
                      <Clock className="w-4 h-4" />
                    )}
                    {formData.participants.length} / {formData.nombre_food_trucks_requis} FT
                  </div>
                </div>
                <Input
                  placeholder="Rechercher un food truck..."
                  value={searchFoodTruck}
                  onChange={(e) => setSearchFoodTruck(e.target.value)}
                  className="mb-2"
                />
                <div className={`grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 border rounded-md max-h-64 overflow-y-auto ${
                  formData.participants.length === formData.nombre_food_trucks_requis 
                    ? "bg-green-50 border-green-200" 
                    : "bg-gray-50"
                }`}>
                  {filteredFoodTrucks.map((ft) => (
                    <div key={ft.id} className={`flex items-center space-x-2 p-2 hover:bg-white rounded ${
                      formData.participants.includes(ft.id) ? "bg-white border border-green-200" : ""
                    }`}>
                      <Checkbox
                        id={`ft-${ft.id}`}
                        checked={formData.participants.includes(ft.id)}
                        onCheckedChange={(checked) => handleParticipantToggle(ft.id, checked)}
                      />
                      <label htmlFor={`ft-${ft.id}`} className="text-sm cursor-pointer flex-1">
                        <span className="font-medium">{ft.nom_food_truck || ft.nom_commercial}</span>
                        <span className="text-muted-foreground ml-1 text-xs">
                          ({ft.specialite || ft.specialites?.[0] || "N/A"})
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
                {/* Message d'état */}
                {formData.participants.length > 0 && (
                  <p className={`text-sm mt-2 ${
                    formData.participants.length === formData.nombre_food_trucks_requis 
                      ? "text-green-600" 
                      : formData.participants.length > formData.nombre_food_trucks_requis
                        ? "text-red-600"
                        : "text-amber-600"
                  }`}>
                    {formData.participants.length === formData.nombre_food_trucks_requis 
                      ? `✓ Nombre de food trucks correct (${formData.participants.length}/${formData.nombre_food_trucks_requis})`
                      : formData.participants.length > formData.nombre_food_trucks_requis
                        ? `✗ Trop de food trucks sélectionnés (${formData.participants.length}/${formData.nombre_food_trucks_requis})`
                        : `⚠ ${formData.nombre_food_trucks_requis - formData.participants.length} food truck(s) restant(s) à sélectionner`
                    }
                  </p>
                )}
              </div>
            )}
            
            {/* Section Redevance - Pour unitaire (Booking Direct & Sondage) */}
            {activeTab === "unitaire" && (
              <div className="border-t pt-4 mt-4">
                <h4 className="font-semibold text-secondary mb-3 flex items-center gap-2">
                  <Euro className="w-4 h-4 text-purple-600" />
                  Configuration Redevance
                </h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Type de prestation</Label>
                    <Select
                      value={formData.type_prestation}
                      onValueChange={(v) => setFormData({ ...formData, type_prestation: v })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Type de prestation" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vente_libre">Vente libre</SelectItem>
                        <SelectItem value="privatisation">Privatisation</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex items-center gap-2 pt-6">
                    <Checkbox
                      id="deal_audrey_unitaire"
                      checked={formData.deal_audrey}
                      onCheckedChange={(v) => {
                        if (v) {
                          const pct = formData.type_prestation === "vente_libre" ? 10 : 7;
                          setFormData({ ...formData, deal_audrey: v, redevance_type: "pourcentage", redevance_pourcentage: pct });
                        } else {
                          setFormData({ ...formData, deal_audrey: v });
                        }
                      }}
                    />
                    <Label htmlFor="deal_audrey_unitaire" className="text-purple-700 font-medium cursor-pointer">
                      Deal Audrey ({formData.type_prestation === "vente_libre" ? "10%" : "7%"})
                    </Label>
                  </div>
                </div>
                
                {!formData.deal_audrey && (
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <Label>Type de redevance</Label>
                      <Select
                        value={formData.redevance_type}
                        onValueChange={(v) => setFormData({ ...formData, redevance_type: v })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pourcentage">Pourcentage (%)</SelectItem>
                          <SelectItem value="fixe">Montant fixe (€)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {formData.redevance_type === "pourcentage" ? (
                      <div>
                        <Label>Pourcentage sur CA HT (%)</Label>
                        <Input
                          type="number"
                          step="0.5"
                          min="0"
                          max="100"
                          value={formData.redevance_pourcentage || ""}
                          onChange={(e) => setFormData({ ...formData, redevance_pourcentage: e.target.value ? parseFloat(e.target.value) : null })}
                          placeholder="Ex: 10"
                          className="mt-1"
                        />
                      </div>
                    ) : (
                      <div>
                        <Label>Montant fixe (€)</Label>
                        <Input
                          type="number"
                          step="1"
                          min="0"
                          value={formData.redevance_fixe || ""}
                          onChange={(e) => setFormData({ ...formData, redevance_fixe: e.target.value ? parseFloat(e.target.value) : null })}
                          placeholder="Ex: 50"
                          className="mt-1"
                        />
                      </div>
                    )}
                  </div>
                )}
                
                {formData.deal_audrey && (
                  <p className="text-sm text-purple-600 mt-2 bg-purple-50 p-2 rounded">
                    Deal Audrey : {formData.type_prestation === "vente_libre" ? "10%" : "7%"} de redevance sur le CA HT déclaré.
                  </p>
                )}
              </div>
            )}
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Informations complémentaires..."
                rows={3}
              />
            </div>
          </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2 flex-shrink-0">
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); setSelectedEvent(null); }}>
              Annuler
            </Button>
            {activeTab === "multiple" ? (
              <div className="flex gap-2">
                <Button variant="outline" disabled={isSubmitting} onClick={() => {
                  // Mode sans sondage - créer directement
                  setFormData({ ...formData, mode_creation: "booking_direct" });
                  setTimeout(() => handleCreateMultiple(), 100);
                }}>
                  <Plus className="w-4 h-4 mr-2" />
                  {isSubmitting ? "Création..." : "Créer sans sondage"}
                </Button>
                <Button disabled={isSubmitting} onClick={() => {
                  // Mode sondage
                  setFormData({ ...formData, mode_creation: "sondage" });
                  setTimeout(() => handleCreateMultiple(), 100);
                }}>
                  <Mail className="w-4 h-4 mr-2" />
                  {isSubmitting ? "Création..." : "Créer et envoyer sondage"}
                </Button>
              </div>
            ) : (
              <Button disabled={isSubmitting} onClick={handleCreateUnitaire}>
                {isSubmitting ? (
                  <>Création...</>
                ) : createMode === "sondage" ? (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Envoyer le sondage
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Créer l'événement
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Detail Dialog for Multiple Events */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedEvent?.titre}</DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Lieu:</span>
                  <p className="font-medium">{selectedEvent.lieu}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Client:</span>
                  <p className="font-medium">{selectedEvent.client || "Non spécifié"}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Couverts estimés:</span>
                  <p className="font-medium">{selectedEvent.couverts_estimes || "Non spécifié"}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Food trucks requis:</span>
                  <p className="font-medium">{selectedEvent.nombre_food_trucks_requis}</p>
                </div>
              </div>
              
              {selectedEvent.dates?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Dates</h4>
                  <div className="space-y-2">
                    {selectedEvent.dates.map((d, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm p-2 bg-gray-50 rounded">
                        <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                        <span>{format(parseISO(d.date), "EEEE d MMMM yyyy", { locale: fr })}</span>
                        <span className="text-muted-foreground">
                          - {d.services?.map(s => `${s.type} (${s.horaire_debut}-${s.horaire_fin})`).join(", ")}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedEvent.description && (
                <div>
                  <h4 className="font-semibold mb-2">Description</h4>
                  <p className="text-sm text-muted-foreground">{selectedEvent.description}</p>
                </div>
              )}
              
              {/* Food trucks intéressés */}
              {selectedEvent.interested_food_trucks?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2 text-green-700">
                    <Users className="w-4 h-4" />
                    Food trucks intéressés ({selectedEvent.interested_food_trucks.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedEvent.interested_food_trucks.map((ft, i) => (
                      <div key={i} className={`flex items-center justify-between p-3 rounded-lg border ${
                        ft.validation_status === 'validated' ? 'bg-green-100 border-green-300' :
                        ft.validation_status === 'rejected' ? 'bg-red-50 border-red-200' :
                        'bg-amber-50 border-amber-200'
                      }`}>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{ft.nom_food_truck}</p>
                            {ft.validation_status === 'validated' && (
                              <Badge className="bg-green-600 text-white text-xs">✓ Validé</Badge>
                            )}
                            {ft.validation_status === 'rejected' && (
                              <Badge className="bg-red-600 text-white text-xs">✗ Rejeté</Badge>
                            )}
                            {!ft.validation_status && (
                              <Badge className="bg-amber-500 text-white text-xs">En attente</Badge>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {ft.specialite && <Badge variant="outline" className="text-xs">{ft.specialite}</Badge>}
                            {ft.specialites?.map((spec, j) => (
                              <Badge key={j} variant="outline" className="text-xs">{spec}</Badge>
                            ))}
                          </div>
                          {ft.staff_evenement && (
                            <p className="text-xs text-muted-foreground mt-1">Staff prévu: {ft.staff_evenement}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right text-sm mr-2">
                            {ft.telephone && (
                              <a href={`tel:${ft.telephone}`} className="text-primary hover:underline block">
                                📞 {ft.telephone}
                              </a>
                            )}
                            {ft.email && (
                              <a href={`mailto:${ft.email}`} className="text-primary hover:underline block text-xs">
                                ✉️ {ft.email}
                              </a>
                            )}
                          </div>
                          {/* Validation buttons - only for Bureau/Admin/Creator and if not yet validated */}
                          {(user?.role === 'admin' || user?.role === 'bureau' || selectedEvent.created_by === user?.id) && 
                           !ft.validation_status && (
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 px-2 border-green-500 text-green-600 hover:bg-green-50"
                                onClick={() => handleValidatePosition(selectedEvent.id, ft.food_truck_id || ft.id, 'validate')}
                                disabled={validatingId === `${selectedEvent.id}_${ft.food_truck_id || ft.id}`}
                              >
                                {validatingId === `${selectedEvent.id}_${ft.food_truck_id || ft.id}` ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <CheckCircle className="w-4 h-4" />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 px-2 border-red-500 text-red-600 hover:bg-red-50"
                                onClick={() => {
                                  const reason = window.prompt("Raison du rejet (optionnel):");
                                  handleValidatePosition(selectedEvent.id, ft.food_truck_id || ft.id, 'reject', reason);
                                }}
                                disabled={validatingId === `${selectedEvent.id}_${ft.food_truck_id || ft.id}`}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Aucun intéressé */}
              {(!selectedEvent.interested_food_trucks || selectedEvent.interested_food_trucks.length === 0) && selectedEvent.sondage_sent && (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 text-center">
                  <p className="text-amber-700">Aucun food truck ne s'est encore positionné</p>
                  <p className="text-sm text-amber-600 mt-1">Le sondage a été envoyé, en attente des réponses...</p>
                </div>
              )}
              
              {/* Client Portal Link */}
              {clientAccessLink && selectedEvent?.id === clientAccessLink.eventId && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200 space-y-2">
                  <p className="text-sm font-medium text-green-800 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Lien d'accès client généré !
                  </p>
                  <div className="flex items-center gap-2">
                    <Input 
                      value={clientAccessLink.link} 
                      readOnly 
                      className="text-xs bg-white"
                    />
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(clientAccessLink.link);
                        toast.success("Lien copié !");
                      }}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => window.open(clientAccessLink.link, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* Password display */}
                  {clientAccessLink.password && (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm font-medium text-amber-800 flex items-center gap-2">
                        <Lock className="w-4 h-4" />
                        Mot de passe à communiquer au client:
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <code className="text-2xl font-mono font-bold tracking-widest bg-white px-4 py-2 rounded border">
                          {clientAccessLink.password}
                        </code>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(clientAccessLink.password);
                            toast.success("Mot de passe copié !");
                          }}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-amber-600 mt-2">
                        Ce mot de passe ne sera plus affiché. Notez-le maintenant.
                      </p>
                    </div>
                  )}
                  
                  <p className="text-xs text-green-600 mt-2">
                    Valide jusqu'au {new Date(clientAccessLink.expiresAt).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Fermer
            </Button>
            <Button 
              variant="outline"
              onClick={() => {
                handleGenerateClientLink(selectedEvent?.id, selectedEvent?.dates ? "organised" : "unitary");
                setClientAccessLink(prev => prev ? { ...prev, eventId: selectedEvent?.id } : { eventId: selectedEvent?.id });
              }}
              disabled={generatingClientLink}
              data-testid="generate-client-link-btn"
            >
              {generatingClientLink ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Link2 className="w-4 h-4 mr-2" />
              )}
              Zone Client
            </Button>
            <Button 
              variant="outline"
              onClick={() => handleOpenClientDocs(selectedEvent)}
              data-testid="view-client-docs-btn"
            >
              <FolderOpen className="w-4 h-4 mr-2" />
              Docs & Photos
            </Button>
            <Button onClick={() => handleSendSondage(selectedEvent?.id, true)} disabled={sendingSondage}>
              <Send className="w-4 h-4 mr-2" />
              Envoyer sondage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Voir Documents & Photos Client */}
      <Dialog open={showClientDocsDialog} onOpenChange={setShowClientDocsDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-[#8B1538]" />
              Documents & Photos - {clientDocsEvent?.titre}
            </DialogTitle>
          </DialogHeader>
          
          {loadingClientDocs ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#8B1538]" />
            </div>
          ) : (
            <Tabs defaultValue="ft-docs" className="flex-1 overflow-hidden flex flex-col">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="ft-docs">
                  <FileText className="w-4 h-4 mr-2" />
                  Docs Food Trucks ({ftDocuments.reduce((sum, ft) => sum + ft.documents_count, 0)})
                </TabsTrigger>
                <TabsTrigger value="client-photos">
                  <Camera className="w-4 h-4 mr-2" />
                  Photos Client ({clientPhotos.length})
                </TabsTrigger>
                <TabsTrigger value="client-menu">
                  <FileText className="w-4 h-4 mr-2" />
                  Menu Client
                </TabsTrigger>
              </TabsList>
              
              {/* Food Trucks Documents */}
              <TabsContent value="ft-docs" className="flex-1 overflow-y-auto mt-4">
                {ftDocuments.length > 0 ? (
                  <div className="space-y-4">
                    {ftDocuments.map((ft) => (
                      <div key={ft.food_truck_id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4 className="font-semibold">{ft.nom_food_truck}</h4>
                            <p className="text-sm text-gray-500">{ft.responsable} - {ft.telephone}</p>
                          </div>
                          <Badge variant="secondary">{ft.documents_count} doc(s)</Badge>
                        </div>
                        
                        {ft.documents.length > 0 ? (
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {ft.documents.map((doc) => (
                              <Button
                                key={doc.type}
                                variant="outline"
                                size="sm"
                                className="justify-start"
                                onClick={() => ouvrirFichier(doc.url).catch(() => toast.error("Document indisponible ou accès refusé"))}
                              >
                                <Download className="w-4 h-4 mr-2" />
                                {doc.label}
                              </Button>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">Aucun document disponible</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Aucun food truck avec documents</p>
                  </div>
                )}
              </TabsContent>
              
              {/* Client Photos */}
              <TabsContent value="client-photos" className="flex-1 overflow-y-auto mt-4">
                {clientPhotos.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {clientPhotos.map((photo) => (
                      <div 
                        key={photo.id}
                        className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100 border cursor-pointer"
                        onClick={() => window.open(`${API}${photo.url}`, '_blank')}
                      >
                        <img
                          src={`${API}${photo.url}`}
                          alt={photo.filename}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                          <Eye className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-2 truncate">
                          {photo.filename}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Image className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Aucune photo uploadée par le client</p>
                  </div>
                )}
              </TabsContent>
              
              {/* Client Menu */}
              <TabsContent value="client-menu" className="flex-1 overflow-y-auto mt-4">
                {clientMenu && (clientMenu.menu_text || clientMenu.menu_file_url) ? (
                  <div className="space-y-4">
                    {clientMenu.menu_text && (
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-medium mb-2">Menu (texte)</h4>
                        <p className="whitespace-pre-wrap text-gray-700">{clientMenu.menu_text}</p>
                      </div>
                    )}
                    
                    {clientMenu.menu_file_url && (
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <h4 className="font-medium mb-2">Fichier Menu</h4>
                        <Button
                          variant="outline"
                          onClick={() => window.open(`${API}${clientMenu.menu_file_url}`, '_blank')}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          {clientMenu.menu_filename || "Télécharger le menu"}
                        </Button>
                      </div>
                    )}
                    
                    {clientMenu.updated_at && (
                      <p className="text-sm text-gray-500">
                        Mis à jour le {new Date(clientMenu.updated_at).toLocaleString('fr-FR')}
                        {clientMenu.updated_by && ` par ${clientMenu.updated_by}`}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Aucun menu fourni par le client</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClientDocsDialog(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Gérer les participants */}
      <Dialog open={showManageParticipantsDialog} onOpenChange={setShowManageParticipantsDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Gérer les Food Trucks - {managingEvent?.titre}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {/* Info */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700">
                <strong>Food trucks requis:</strong> {managingEvent?.nombre_food_trucks_requis || 1}
                <span className="mx-2">|</span>
                <strong>Sélectionnés:</strong> {selectedParticipants.length}
              </p>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Rechercher un food truck..."
                value={participantSearch}
                onChange={(e) => setParticipantSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Food trucks intéressés (prioritaires) */}
            {managingEvent?.interested_food_trucks?.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-green-700 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Food trucks intéressés ({managingEvent.interested_food_trucks.length})
                </h4>
                <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-2 bg-green-50">
                  {managingEvent.interested_food_trucks.map((ft, i) => {
                    const ftData = foodTrucks.find(f => f.id === ft.food_truck_id);
                    const isSelected = selectedParticipants.includes(ft.food_truck_id);
                    const ftName = ft.nom_food_truck || ftData?.nom_commercial || ftData?.nom_food_truck || ftData?.nom || "Food Truck";
                    return (
                      <div 
                        key={i} 
                        className={`flex items-center justify-between p-2 rounded border cursor-pointer transition-colors ${
                          isSelected ? 'bg-green-100 border-green-400' : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                        onClick={() => toggleParticipant(ft.food_truck_id)}
                      >
                        <div className="flex items-center gap-2">
                          <Checkbox checked={isSelected} />
                          <span className="font-medium">{ftName}</span>
                          {(ft.specialite || ftData?.specialite) && <Badge variant="secondary" className="text-xs">{ft.specialite || ftData?.specialite}</Badge>}
                        </div>
                        <Badge className="bg-green-100 text-green-700 text-xs">Intéressé</Badge>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Tous les food trucks */}
            <div className="space-y-2">
              <h4 className="font-medium text-gray-700 flex items-center gap-2">
                <Truck className="w-4 h-4" />
                Tous les food trucks actifs
              </h4>
              <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-2">
                {foodTrucks
                  .filter(ft => {
                    if (!participantSearch) return true;
                    const search = participantSearch.toLowerCase();
                    const ftName = ft.nom_commercial || ft.nom_food_truck || ft.nom || ft.name || "";
                    return ftName.toLowerCase().includes(search) || 
                           ft.specialite?.toLowerCase().includes(search);
                  })
                  .map(ft => {
                    const isSelected = selectedParticipants.includes(ft.id);
                    const isInterested = managingEvent?.interested_food_trucks?.some(i => i.food_truck_id === ft.id);
                    const ftName = ft.nom_commercial || ft.nom_food_truck || ft.nom || ft.name || "Food Truck sans nom";
                    return (
                      <div 
                        key={ft.id} 
                        className={`flex items-center justify-between p-2 rounded border cursor-pointer transition-colors ${
                          isSelected ? 'bg-primary/10 border-primary' : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                        onClick={() => toggleParticipant(ft.id)}
                      >
                        <div className="flex items-center gap-2">
                          <Checkbox checked={isSelected} />
                          <span className={`font-medium ${isSelected ? 'text-primary' : ''}`}>{ftName}</span>
                          {ft.specialite && <Badge variant="secondary" className="text-xs">{ft.specialite}</Badge>}
                        </div>
                        <div className="flex items-center gap-2">
                          {isInterested && <Badge className="bg-green-100 text-green-700 text-xs">Intéressé</Badge>}
                          {isSelected && <CheckCircle className="w-4 h-4 text-primary" />}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
          
          <DialogFooter className="border-t pt-4">
            <div className="flex items-center justify-between w-full">
              <span className="text-sm text-muted-foreground">
                {selectedParticipants.length} food truck(s) sélectionné(s)
              </span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowManageParticipantsDialog(false)}>
                  Annuler
                </Button>
                <Button onClick={handleSaveParticipants}>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Enregistrer
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Gérer par date (Planning Multiple) */}
      <Dialog open={showManageDateDialog} onOpenChange={setShowManageDateDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-purple-600" />
              Gérer par date - {managingDateEvent?.titre}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {/* Info */}
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-700">
                Sélectionnez les food trucks pour chaque date. Vous pouvez aussi envoyer un sondage ciblé par date.
              </p>
            </div>

            {/* Liste des dates */}
            {managingDateEvent?.dates?.map((dateItem, dateIndex) => (
              <Card key={dateIndex} className="border-purple-200">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <CalendarIcon className="w-4 h-4 text-purple-600" />
                      {format(parseISO(dateItem.date), "EEEE dd MMMM yyyy", { locale: fr })}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {(participantsByDate[dateItem.date] || []).length} FT sélectionné(s)
                      </Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSendSondageForDate(managingDateEvent.id, dateItem.date)}
                        disabled={sendingSondage}
                        className="text-xs"
                      >
                        <Mail className="w-3 h-3 mr-1" />
                        Sondage
                      </Button>
                    </div>
                  </div>
                  {dateItem.services && (
                    <div className="flex gap-2 mt-1">
                      {dateItem.services.map((service, j) => (
                        <Badge key={j} variant="secondary" className="text-xs">
                          {service.type === "midi" ? "🌞 Midi" : "🌙 Soir"} {service.horaire_debut}-{service.horaire_fin}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardHeader>
                <CardContent className="pt-2">
                  {/* Liste des FT à sélectionner */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                    {foodTrucks.map(ft => {
                      const isSelected = (participantsByDate[dateItem.date] || []).includes(ft.id);
                      const isInterested = managingDateEvent?.interested_food_trucks?.some(i => i.food_truck_id === ft.id);
                      const ftName = ft.nom_commercial || ft.nom_food_truck || ft.nom || ft.name || "Food Truck";
                      return (
                        <div 
                          key={ft.id} 
                          className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors text-sm ${
                            isSelected ? 'bg-purple-100 border-purple-400' : 'bg-white border-gray-200 hover:bg-gray-50'
                          }`}
                          onClick={() => toggleDateParticipant(dateItem.date, ft.id)}
                        >
                          <Checkbox checked={isSelected} className="pointer-events-none" />
                          <span className={`truncate ${isSelected ? 'font-medium text-purple-700' : ''}`}>
                            {ftName}
                          </span>
                          {isInterested && <span className="text-green-500 text-xs">✓</span>}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <DialogFooter className="border-t pt-4">
            <div className="flex items-center justify-between w-full">
              <span className="text-sm text-muted-foreground">
                {Object.values(participantsByDate).flat().length} sélection(s) au total
              </span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowManageDateDialog(false)}>
                  Annuler
                </Button>
                <Button onClick={handleSaveDateParticipants} className="bg-purple-600 hover:bg-purple-700">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Enregistrer
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
